import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import {
  PencilIcon,
  Cog6ToothIcon,
  LifebuoyIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { doc, updateDoc, getDoc, setDoc, onSnapshot, collection, query, where, getDocs } from "firebase/firestore";
import { auth, db, storage } from "../firebase";
import { updateProfile, signOut } from "firebase/auth";
import Configuracion from "./OptionsProfile/Configuration";
import Ayuda from "./OptionsProfile/Help";
import Soporte from "./OptionsProfile/Supports";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import {BriefcaseIcon} from "@heroicons/react/24/outline";
import PromotionalProfileModal from "./PromotionalProfileModal";
import EditarPerfilesPromocionales from "./EditPromocionalProfiles";
import RenovarMembresia from "./RenewMembership";
import ManagePromotionalPosts from "./ManagePromotionalPosts/ManagePromotionalPosts";
import { FaStar } from "react-icons/fa";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";


export default function ProfileModal({ isOpen, onClose }) {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState({});
  const [nickname, setNickname] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [uploading, setUploading] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const fileInputRef = useRef();
  const [showPromotionalModal, setShowPromotionalModal] = useState(false);
  const [hasPromoProfiles, setHasPromoProfiles] = useState(false);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false); 
  const [bio, setBio] = useState("");
  const [showManagePromotionalPosts, setShowManagePromotionalPosts] = useState(false);
  const [starCount, setStarCount] = useState(0);




  useEffect(() => {
    const checkPromoProfiles = async () => {
      if (!currentUser) return;
      setLoadingProfiles(true);

      try {
        const promoRef = collection(db, "promoProfiles"); 
        const q = query(
          promoRef,
          where("userId", "==", currentUser.uid),
          where("status", "==", "COMPLETED")
        );
        const snapshot = await getDocs(q /*, { source: "server" }*/);
        setHasPromoProfiles(!snapshot.empty);
      } catch (error) {
        console.error("Error fetching promo profiles:", error);
        setHasPromoProfiles(false);
      } finally {
        setLoadingProfiles(false);
      }
    };

    if (isOpen && currentUser) {
      checkPromoProfiles();
    }
  }, [isOpen, currentUser]);

  //Estrellas
  useEffect(() => {
    if (!currentUser?.uid) return;

    const starsRef = collection(db, "profiles", currentUser.uid, "stars");

    const unsubscribe = onSnapshot(starsRef, (snapshot) => {
      setStarCount(snapshot.size); // snapshot.size es la cantidad de documentos
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);


  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return;

      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        const newUserData = {
          nombre:      currentUser.displayName || "",
          correo:      currentUser.email || "",
          carrera:     "",
          role:        "Estudiante",
          status:      "pending",
          nickname:    "",
          photoURL:    currentUser.photoURL || "",
        };
        await setDoc(userRef, newUserData);
        setUserData(newUserData);
        setNickname("");
        setPhotoURL(newUserData.photoURL);
      } else {
        const currentData = userSnap.data();
        setUserData(currentData);
        setNickname(currentData.nickname || "");
        setPhotoURL(currentData.photoURL || currentUser.photoURL || "");
        setBio(currentData.biografia || "");
      }
    };

    if (isOpen) {
      fetchUserData();
    }
  }, [isOpen, currentUser]);

  const handlePhotoChange = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  try {
    setUploading(true);

    // Crear ruta única en Firebase Storage
    const filePath = `foto_de_perfil/${currentUser.uid}_${Date.now()}_${file.name}`;
    const storageRef = ref(storage, filePath);

    // Subir archivo
    const snapshot = await uploadBytes(storageRef, file);

    // Obtener URL pública
    const imageUrl = await getDownloadURL(snapshot.ref);

    // Actualizar Firestore
    const userRef = doc(db, "users", currentUser.uid);
    await updateDoc(userRef, { photoURL: imageUrl });

    // Actualizar Firebase Auth
    await updateProfile(currentUser, { photoURL: imageUrl });

    // Actualizar estado local
    setPhotoURL(imageUrl);
    setUserData((prev) => ({ ...prev, photoURL: imageUrl }));
  } catch (error) {
    console.error("Error al subir la imagen de perfil:", error);
  } finally {
    setUploading(false);
  }
};

  const toggleSection = (section) => {
    setActiveSection((prev) => (prev === section ? null : section));
  };

  const handleSaveNickname = async () => {
    if (!nickname.trim()) return;
    const userRef = doc(db, "users", currentUser.uid);
    await updateDoc(userRef, { nickname });
    setUserData((prev) => ({ ...prev, nickname }));
  };

  const handleSaveBio = async () => {
    const userRef = doc(db, "users", currentUser.uid);
    await updateDoc(userRef, { biografia: bio });
    setUserData((prev) => ({ ...prev, biografia: bio }));
  };

  const handleLogout = async () => {
    await signOut(auth);
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="scale-95 opacity-0"
              enterTo="scale-100 opacity-100"
              leave="ease-in duration-200"
              leaveFrom="scale-100 opacity-100"
              leaveTo="scale-95 opacity-0"
            >
              <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                <Dialog.Title className="flex items-center justify-between text-lg font-bold text-gray-800">
                  <span>Tu Perfil</span>
                  <div className="flex items-center space-x-1">
                    <FaStar className="w-5 h-5 text-yellow-400" />
                    <span className="text-sm font-medium">{starCount}</span>
                  </div>
                </Dialog.Title>

                {/* Foto de perfil */}
                <div className="mt-4 relative w-24 h-24 mx-auto">
                  <img
                    src={
                      photoURL ||
                      currentUser?.photoURL ||
                      "https://via.placeholder.com/100"
                    }
                    alt="Foto de perfil"
                    className="rounded-full w-24 h-24 object-cover"
                  />
                  <button
                    className="absolute bottom-0 right-0 bg-white rounded-full p-1 shadow"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <PencilIcon className="h-5 w-5 text-gray-600" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handlePhotoChange}
                  />
                </div>

                {uploading && (
                  <p className="text-sm text-blue-500 text-center mt-2">
                    Subiendo imagen...
                  </p>
                )}

                {/* Datos personales */}
                <div className="mt-6 space-y-2 text-center">
                  <p className="text-lg font-semibold text-gray-900">
                    {userData.nombre || "Nombre no disponible"}
                  </p>
                  <p className="text-sm text-gray-600">
                    {userData.correo || currentUser?.email}
                  </p>
                  {/* Badge de estado de verificación */}
                  {(() => {
                    const s = userData.status;
                    if (!s || s === "verified") {
                      return (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full border border-green-300">
                          ✅ Verificado
                        </span>
                      );
                    }
                    if (s === "pending") {
                      return (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full border border-yellow-300">
                          ⏳ Verificación pendiente
                        </span>
                      );
                    }
                    if (s === "rejected") {
                      return (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full border border-red-300">
                          ❌ Cuenta rechazada
                        </span>
                      );
                    }
                    return null;
                  })()}
                  <p className="inline-block mt-1 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                    {userData.carrera || "Carrera no especificada"}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Rol: <strong>{userData.role || "—"}</strong>
                  </p>
                </div>

                {/* Nickname */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700">
                    Apodo público
                  </label>
                  <input
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                  <button
                    onClick={handleSaveNickname}
                    className="mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    Guardar apodo
                  </button>
                   {/* Biografía */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Biografía
                    </label>
                    <textarea
                      rows={3}
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      placeholder="Escribe algo sobre ti..."
                    />
                    <button
                      onClick={handleSaveBio}
                      className="mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                      Guardar biografía
                    </button>
                  </div>
                </div>

                {/* Opciones */}
                {/* Crear perfil Promocional */}
                <div>
                  <button
                    onClick={() => toggleSection("promocional")}
                    className="flex items-center justify-between w-full px-4 py-2 rounded hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      <BriefcaseIcon className="h-5 w-5 mr-2" />
                      Perfil Promocional
                    </div>
                    {activeSection === "promocional" ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </button>

                  {activeSection === "promocional" && (
                    <div className="px-4 py-2 text-sm text-gray-600 space-y-4">
                      {loadingProfiles ? (
                        <p className="text-gray-400">Cargando perfiles...</p>
                      ) : hasPromoProfiles ? (
                        <>
                          <p className="text-gray-700">
                            Gestiona tus perfiles y publicaciones promocionales activas desde aquí. Mantén tu presencia siempre actualizada.
                          </p>

                          <div className="space-y-2">
                            <button
                              onClick={() => setShowManagePromotionalPosts(true)}
                              className="w-full sm:w-auto px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded"
                            >
                              Administrar Publicaciones Promocionales
                            </button>

                            <button
                              onClick={() => setShowProfileEditor(true)}
                              className="w-full sm:w-auto px-4 py-2 text-white bg-green-600 hover:bg-green-700 rounded"
                            >
                              Editar mis perfiles promocionales
                            </button>

                            <button
                              onClick={() => {
                                setShowRenewModal(true);
                                setShowPromotionalModal(false);
                                setShowProfileEditor(false);
                              }}
                              className="w-full sm:w-auto px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded"
                            >
                              Renovar membresía
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <p>
                            Crea tu perfil promocional para destacar tus proyectos o emprendimientos de forma llamativa. 
                            ¡Es la mejor forma de atraer atención dentro de la plataforma!
                          </p>

                          <button
                            onClick={() => setShowPromotionalModal(true)}
                            className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded"
                          >
                            Crear mi perfil promocional
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Modal para Administrar Publicaciones Promocionales */}
                {showManagePromotionalPosts && (
                  <ManagePromotionalPosts onClose={() => setShowManagePromotionalPosts(false)} />
                )}


                {/* Configuración */}
                <div>
                  <button
                    onClick={() => toggleSection("config")}
                    className="flex items-center justify-between w-full px-4 py-2 rounded hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      <Cog6ToothIcon className="h-5 w-5 mr-2" />
                      Configuración
                    </div>
                    {activeSection === "config" ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </button>
                  {activeSection === "config" && <Configuracion />}
                </div>

                {/* Ayuda */}
                <div>
                  <button
                    onClick={() => toggleSection("ayuda")}
                    className="flex items-center justify-between w-full px-4 py-2 rounded hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      <QuestionMarkCircleIcon className="h-5 w-5 mr-2" />
                      Ayuda
                    </div>
                    {activeSection === "ayuda" ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </button>
                  {activeSection === "ayuda" && <Ayuda />}
                </div>

                {/* Soporte */}
                <div>
                  <button
                    onClick={() => toggleSection("soporte")}
                    className="flex items-center justify-between w-full px-4 py-2 rounded hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      <LifebuoyIcon className="h-5 w-5 mr-2" />
                      Soporte
                    </div>
                    {activeSection === "soporte" ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </button>

                  {/*  Pasa nombre y uid al hijo */}
                  {activeSection === "soporte" && (
                    <Soporte
                      user={{
                        nombre:
                          userData.nombre || currentUser?.displayName || "Desconocido",
                        uid: currentUser?.uid || "Sin UID",
                      }}
                    />
                  )}
                </div>

                {/* Cerrar sesión */}
                <div className="mt-6">
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-red-600 rounded hover:bg-red-50"
                  >
                    Cerrar sesión
                  </button>
                </div>

                {/* Cerrar modal */}
                <div className="mt-4 text-right">
                  <button
                    className="text-sm text-gray-500 hover:text-gray-800"
                    onClick={onClose}
                  >
                    Cerrar
                  </button>
                </div>
                <PromotionalProfileModal
                  isOpen={showPromotionalModal}
                  onClose={() => setShowPromotionalModal(false)}
                  userId={currentUser?.uid}
                />
                <EditarPerfilesPromocionales
                  isOpen={showProfileEditor}
                  onClose={() => setShowProfileEditor(false)}
                  userId={currentUser?.uid}
                />

                <RenovarMembresia
                  isOpen={showRenewModal}
                  onClose={() => setShowRenewModal(false)}
                  userId={currentUser?.uid}
                />

              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
